import {ChartTypeEnum} from  "@/components/explorer/visualizations/ChartTypeEnum.js"
import * as Accessibility from "@/components/accessibility/accessibility.js";
import markdownit from 'markdown-it'
import { event } from 'vue-gtag'
import  * as AppliedValues from "@/components/explorer/visualizations/AppliedValues.js";

const version = __APP_VERSION__

const md = markdownit()

class ChartSettings {
    constructor(xAxisDim, seriesDim, type) {
        this.xAxisDim = xAxisDim;
        this.seriesDim = seriesDim;
		this.type = type;
    }
}

async function defineChartProps(type, indicatorObj, chartSettings, { onMoreClick }) {
	let yValue = indicatorObj.valuesLabel;
	let allFilters = getCategoryFilters(indicatorObj)

	chartSettings.request =  AppliedValues.buildRequest(indicatorObj, chartSettings, yValue, allFilters);
	AppliedValues.applyRequestFilters(indicatorObj, type, chartSettings.request)

	chartSettings.request.categories.forEach(cat => {
		if(cat.filters.length == 0) {
			throw new Error("No records found");
		}
	})

	let results = await fetchData(chartSettings.request)

	if(Object.keys(results).length == 0 || Object.keys(Object.values(results)[0]).length == 0) {
	 	throw new Error("No records found");
	} else {
		let noData = true;
		const isEmpty = (obj) => Object.keys(obj).length === 0;

		Object.entries(results).forEach(([key, item]) => {
			const truthyOnly = Object.fromEntries(
				Object.entries(item).filter(([_, value]) => !!value)
			);
			if(!isEmpty(truthyOnly)) {
				noData = false;
			}
			//console.log(key, noData)
		});
		if(noData) {
			throw new Error("No records found");
		}
	}

	let props = buildChartJson(type, indicatorObj, yValue, chartSettings, results, { onMoreClick })
	return props;
}

async function fetchData(request) {
	const apiUrl = import.meta.env.VITE_API_URL;

	let reqDims = request.xAxis + ',';
	let reqFilters = '';
	request.categories.forEach((cat) => {
		if(cat.selected) {
			reqDims += cat.categoryName;
		}
		reqFilters += cat.categoryName + ': ' + cat.filters.join() + '|';
	})
	event('filter_dataset', { 'indicator' : request.datasetName, 'dimensions': reqDims, 'filter' : reqFilters.slice(0,100).slice(0,-1) })

	let url = apiUrl + '/indicators/api/chartdata'
	let response = await fetch(url, {
		method: 'POST',
		headers: {
			'Content-Type': 'application/json',
			'x-api-version': version
		},
			body: JSON.stringify(request)
		}
	)
	if (!response.ok) {
		throw new Error(`HTTP status: ${response.status}`);
	}
	return await response.json()
}

const buildChartJson = (type, indicatorObj, yValue, chartSettings, result, { onMoreClick }) => {
	
	const valuesUnit = indicatorObj.valuesUnit	
	const usdFormatter = new Intl.NumberFormat('en-US', {style: 'currency', currency: 'USD', minimumFractionDigits: indicatorObj.decimalPrecision, maximumFractionDigits: 2});
	const pctFormatter = new Intl.NumberFormat('en-US', {minimumFractionDigits: indicatorObj.decimalPrecision, maximumFractionDigits: 2});
	const floatFormatter = new Intl.NumberFormat('en-US', {minimumFractionDigits: indicatorObj.decimalPrecision, maximumFractionDigits: 100});
	const numFormatter = new Intl.NumberFormat('en-US', {minimumFractionDigits: 0, maximumFractionDigits: 100});
	//TODO: Enable new formatter logic if Float valuesUnit implemented
	let formatter = valuesUnit == "Currency"?numFormatter:valuesUnit == "Percent"?pctFormatter:valuesUnit=="Float"?floatFormatter:numFormatter
	//let formatter = valuesUnit == "Currency"?numFormatter:valuesUnit == "Percent"?pctFormatter:numFormatter
	const valuesMultiplier = (indicatorObj.chartMultiplier ? parseInt(indicatorObj.chartMultiplier) : 1);
	//const valuesRoundingUnit = (indicatorObj.roundingUnit && indicatorObj.roundingUnit.length > 0 ? '(' + indicatorObj.roundingUnit.replace(/[$%]/g, "") + ')' : null);
	const valuesRoundingUnit = (indicatorObj.roundingUnit && indicatorObj.roundingUnit.length > 0 ? '(' + indicatorObj.roundingUnit + ')' : null);

	let ctype = type != null?type:'sankey'
   	let chartprops = {
		lang: {
			locale: 'en-US'
		},
		chart: {
			type: ctype,
			styledMode: true,
			// Explicitly tell the width and height of a chart
			//height: 560,
			height: 0,
			width: null,
			events: {
				exportData: function (e) {
					// Remove the first column (internal Highcharts ID)
					e.dataRows.forEach(row => {
						row.splice(0, 1);
					});

					// Also fix header row
					if (e.dataRows.length) {
						e.dataRows[0].splice(0, 1);
					}
				},
				render() {
					//add Full Source button link to footer to support mobile format
					const links = this.container.querySelectorAll('.footnote-more');
					if(links != null) {
						links.forEach(link => {
							if (link && !link.dataset.bound) {
								link.dataset.bound = 'true';

								link.addEventListener('click', (e) => {
									e.preventDefault();
									console.log('clicked', e)
									onMoreClick(e)
								});
							}
						})
					}

					//reformat title/subtitle to support wrapped labels in mobile layout
					const chart = this;
					if (!chart.title || !chart.subtitle) {
						return;
					}
					const titleBBox = chart.title.getBBox();
					if(titleBBox.height < 30) {
						chart.subtitle.attr({
							y: chart.title.alignAttr.y + titleBBox.height 
						});
					} else {
						chart.title.attr({
							y: titleBBox.height - 50
						});						
						chart.subtitle.attr({
							y: titleBBox.height
						});
					}
					
					const caption = this.caption;
					if (caption && caption.css) {
						caption.css({
							width: (this.chartWidth - 40) + 'px'
						});
					}					
				}
			}
		},
		credits: {
			enabled: false
		},
		title: {
			text: '',
		},
		subtitle: {
			text: ''
		},		
		legend: {
			//align: "left",
			maxHeight: 150,
			title: {
				text: ''
			}
		},
		series: [],
		responsive: {
			rules: [{
				condition: {
					maxWidth: 600
				},
				chartOptions: {
					legend: {
						enabled: false
					}
				}
			}]
		},
		exporting: {
			chartOptions: {
				chart: {
					type: ctype,
					style: {
						//prevents warning on fetching font files that are not needed
						fontFamily: 'Roboto,sans-serif'
					},
					spacingBottom: 10
				},
				series: [{
					dataLabels: {
						useHTML: false
					}
				}]
			},
			buttons: {
			 	contextButton: {
			 		enabled: false // hides the built-in button
			 	}
			},
			local: true,
			libURL: './highcharts-libs/',
			fallbackToExportServer: false, // use offline export only
			applyStyleSheets: true,
			useHTML: false
		},
		tooltip: {
			nodeFormatter: function () {
				return false;
			},
			formatter: function () {
				if(this.isNode) {
					return false;
				} else {

					let title = yValue;
					if(valuesRoundingUnit) {
						title += ' ' + valuesRoundingUnit;
					}
					let header =`<div class="header"><span class="title">${title}</span>`;
					header += 
					`	<div class="label">${chartSettings.seriesDim.columnName}:</div>
					</div>`;

					//TODO: Add '$' prefix
					const value = valuesUnit == "Currency" ? floatFormatter.format(this.weight / valuesMultiplier) : numFormatter.format(this.weight / valuesMultiplier);
					//const value = numFormatter.format(this.weight / valuesMultiplier);
					const icon = '<span class="tt-circle ' + this.className + '"><svg width="10px" height="10px"><circle r="5px" cx="5px" cy="5px"/></svg></span>';
					const body = `${icon}<span class="explorer-tool-tip">${this.fromNode.name} \u2192 ${this.toNode.name}: <b>${value}</b></span>`
					return `<div class="explorer-tool-tip">${header}${body}</div>`;
				}
			},
			useHTML: true,
			outside: true,
			shared: true // or true depending on default or user config
		}
  	}

	//chartprops.title.text = indicatorObj.valuesLabel;
	/*if(valuesRoundingUnit) {
		chartprops.title.text += ' ' + valuesRoundingUnit;
	}*/
	let title = `${indicatorObj.valuesLabel} by ${chartSettings.xAxisDim.columnName} and ${chartSettings.seriesDim.columnName}`
	let year = indicatorObj.columns.items.find(x => x.columnName == 'Year')
	if(year) {
		const minYear = Math.min(...year.appliedValues.filter(x => x.selected).map(item => Number(item.name)));
		const maxYear = Math.max(...year.appliedValues.filter(x => x.selected).map(item => Number(item.name)));
		if(minYear == maxYear)
			title += ": " + minYear
		else
			title += ": " + minYear + "\u2013" + maxYear
	}
	chartprops.title.text = title

	let filters = ''
	let cols = 	indicatorObj.columns.items.filter(c => {
		if(c.valueFlag == 0 && c.categoryLabelFlag == 1 && c.columnName != 'Year'
			&& c.columnName != chartSettings.xAxisDim.columnName
			&& c.columnName != chartSettings.seriesDim.columnName
			&& c.columnName != chartSettings.seriesDim.parentColumnName
			&& c.columnName != chartSettings.seriesDim.childColumnName
			&& c.childColumnName == null) {
				return c
			}
		})
	cols.forEach((c, n) => {
		let selected = "<span class='section-break'>" + c.columnName + ": "
		if(c.appliedValues && c.appliedValues.length > 0) {
			let all = c.totalLabel 
			let values = c.appliedValues.filter(x => x.selected && x.name != all)
			if(values.length > 0) {
				values.forEach((v, i) => selected += v.name + (i < values.length-1?', ': n < cols.length -1?'; ':''))
				filters += selected + "</span>"
			}
		}
	})
	chartprops.subtitle.text = filters	

	let availableData = Object.entries(result);

	//Build series array which contains all data point for a bar
	let catName = chartSettings.seriesDim.columnName
	//chartprops.title.text = chartSettings.xAxisDim.columnName;

	let keys = ['from', 'to', 'weight'];
	let nodes = [];
	let data = [];
	let seriesObj = {};
	seriesObj.type = "sankey";
	seriesObj.name = catName;
	seriesObj.dataLabels = {
			enabled: true,
			className: 'indicators-sankey-labels indicators-sankey-labels-node',
			//format: {},
			//nodeFormat: '<span class="indicators-sankey-labels indicators-sankey-labels-node">{point.name}</span>',
			nodeFormat: '{point.name}',
			useHTML: true
		};

	let b = 0;
	availableData.forEach(([cat, obj]) => {
		//node:
		if(nodes.some((node) => node.id === chartSettings.seriesDim.columnName + '_' + cat.replaceAll(' ','-'))) {
		} else {
			//console.log('NEW NODE cat',chartSettings.seriesDim.columnName + '_' + cat.replaceAll(' ','-'))
			let indicatorsClassName = 'indicators-sankey indicators-chart-series-' + b;
			if(Accessibility.readUserPreference('colorblind') === 'achromatopsia') {
				indicatorsClassName = 'indicators-sankey indicators-chart-series-' + b % 10;
			}
			let node_obj = {id: chartSettings.seriesDim.columnName + '_' + cat.replaceAll(' ','-'), name: cat, className: indicatorsClassName};
			nodes.push(node_obj);
			b++;
		}
		//obj:
		Object.entries(obj).forEach(([key, val]) => {
			//node:
			if(nodes.some((node) => node.id === chartSettings.xAxisDim.columnName + '_' + key.replaceAll(' ','-'))) {
			} else {
				//console.log('NEW NODE key',chartSettings.xAxisDim.columnName + '_' + key.replaceAll(' ','-'))
				let indicatorsClassName = 'indicators-sankey indicators-chart-series-' + b;
				if(Accessibility.readUserPreference('colorblind') === 'achromatopsia') {
					indicatorsClassName = 'indicators-sankey indicators-chart-series-' + b % 10;
				}
				let node_obj = {id: chartSettings.xAxisDim.columnName + '_' + key.replaceAll(' ','-'), name: key, className: indicatorsClassName};
				nodes.push(node_obj);
				b++;
			}
			//data:
			const theValue = !isNaN(val) ? parseFloat(val) * valuesMultiplier : Number(val.replaceAll(",", "")) * valuesMultiplier;
			const data_obj = {
				'from': chartSettings.seriesDim.columnName + '_' + cat.replaceAll(' ','-'),
				'to': chartSettings.xAxisDim.columnName + '_' + key.replaceAll(' ','-'),
				'weight': !isNaN(theValue) ? theValue : 0
			}
			const matchNode = nodes.find((node) => node.id === chartSettings.seriesDim.columnName + '_' + cat.replaceAll(' ','-'));
			if(matchNode) {
				data_obj.className = matchNode.className;
			}
			data.push(data_obj);
		});

	});

	//console.log('nodes:',nodes)
	//console.log('data:',data)
	//seriesObj.keys = keys;
	seriesObj.nodes = nodes;
	seriesObj.data = data;
	chartprops.series.push(seriesObj);

 	const abbr = indicatorObj.abbreviations ? indicatorObj.abbreviations.join('; ') + '.' : '';
	const src = md.renderInline(indicatorObj.source);
	//srcHtml is used in the footer mobile modal
	chartSettings.srcHtml = src
	chartSettings.keyHtml = indicatorObj.abbreviations ? indicatorObj.abbreviations.join('.<br />') + '.' : '';

	let footerNotes = `<div class="explorer-footnote-wrapper">`
	if(indicatorObj.abbreviations && indicatorObj.abbreviations.length > 0) {
		footerNotes += `	<span class="explorer-footnote"><span class="title">Key:</span> ${abbr} </span>`
		footerNotes += `	<button class="btn-tertiary footnote-more" data-value='abbreviations'>Full Key</button><br />`
	}
	footerNotes += `	<button class="btn-tertiary explorer-footnote-link" data-value='source'>See full data notes</button><br/>`
	footerNotes += `	<span class="explorer-footnote"><span class="title">Source(s):</span> ${src} </span>`
	footerNotes += `	<button class="btn-tertiary footnote-more" data-value='source'>Full Source</button>`
	footerNotes += `</div>`

  	let footerNotesExp = `<div class="explorer-footnote-wrapper">`
	if(indicatorObj.abbreviations && indicatorObj.abbreviations.length > 0) {
		footerNotesExp += `<span class="explorer-footnote"><span class="title">Key:</span> ${abbr} </span><br/>`
	}
	footerNotesExp += `<span class="explorer-footnote"><span class="title">Source(s):</span> ${src} </span></div>`

	let footer = {}
	footer.align = "left"
	footer.verticalAlign = "bottom"
	footer.useHTML = true
	footer.text = footerNotes
	chartprops.caption = footer;

	let footerExp = {}
	footerExp.align = "left"
	footerExp.verticalAlign = "bottom"
	footerExp.useHTML = false
	footerExp.text = footerNotesExp
	chartprops.exporting.chartOptions.caption = footerExp;

	return chartprops
}

async function refreshSankeyChart(type, chart, indicatorObj, chartSettings, dim, { onMoreClick }) {
	if(chart != null) {
		//all non xAxis series dim need to retrieve new dataset that meets filter criteria and rebuild chart
		let props = await defineChartProps(type, indicatorObj, chartSettings, { onMoreClick })
		return props
	}
}

function getCategoryFilters(indicator) {
	let columns = indicator.columns.items.filter(col => {
		if(col.categoryLabelFlag)
			return col;
	})
	return columns
}

//Set Xaxis to first dim, if it is a year limit Xaxus to the LATEST (edit:Tom) year. For all other dims, show all values
function getSankeyChartSettings(indicator, type) {
	console.log('getSankeyChartSettings')
	AppliedValues.setupAppliedValues(indicator)

	let seriesDim = indicator.columns.items.find(col => {
		if(col.sankeyFlag == 1) {
			return col;
		}
	})
	if(seriesDim == null) {
		throw new Error("Indicator categories are not valid for use as a series")
	}
	let xAxisDim = indicator.columns.items.find(col => {
		if(col.categoryLabelFlag == 1 &&
			col.columnName !== 'Year' &&
			col.columnName !== seriesDim.columnName &&
			(col.parentColumnName === null || col.parentColumnName !== seriesDim.columnName) &&
			(col.childColumnName === null || col.childColumnName !== seriesDim.columnName)) {
			return col;
		}
	})
	if(xAxisDim == null) {
		throw new Error("Indicator categories are not valid for use on the X Axis")
	}

	//set dim values used in dropdowns
	setDropdownDimValues(xAxisDim)
	setDropdownDimValues(seriesDim)

	//setup non-dropdown dims values
	indicator.columns.items.forEach(col => {
		//col is a data filter, so only allow one value to be selected

		if(col.categoryLabelFlag && col.columnName !== xAxisDim.columnName && col.columnName !== seriesDim.columnName) {
			console.log('getSankeyChartSettings col:',col.columnName)
			const defaultVals = col.defaultValues.filter(x => col.distinctValues.includes(x))
			let val = null
			if (defaultVals.length > 0) {
				//use default value is no vals selected
				val = col.appliedValues.find(s => s.name == defaultVals[0])
			} else {
				const allVal = col.totalLabel
				const totalLabelFound = col.distinctValues.includes(allVal)
				if(totalLabelFound) {
					val = col.appliedValues.find(s => s.name == allVal)
				}
			}
			//If no default set and no total values set, use first value
			if(val == null) {
				//unless the columnName = Year then use the last value
				if(col.columnName === 'Year') {
					val = col.appliedValues[col.appliedValues.length - 1]
				} else {
					val = col.appliedValues[0]
				}
			}
			col.appliedValues.forEach(x => x.selected = x.name == val.name?true:false)
		}
	})
	return new ChartSettings(xAxisDim, seriesDim, type)
}

function setDropdownDimValues(col) {
	// AppliedValues.setupAppliedValues(indicator) already looks for defaults
	// Special rule for Treemap and Sankey sets dims to all values except totals
	const allVal = col.totalLabel
	const allValList = allVal.split(',').map(item => item.trim());
	const allValset = new Set(allValList);
	let totalsLabels = col.distinctValues.filter(item => allValset.has(item)); // [3, 4]
	if(totalsLabels.length > 0) {
		col.appliedValues.forEach(x => x.selected = totalsLabels.includes(x.name)?false:true)
	} else {
		try{
			Object.entries(col.distinctValues[0]).forEach(([key, arr]) => {
				const result = arr.filter(item => allValset.has(item));
				totalsLabels.push(...result);
			})
			col.appliedValues.forEach(x => x.selected = totalsLabels.includes(x.name)?true:false)
		} catch(e) {
			//don't do anything on failure
		}
	}
	return
}

function updateSankeyChartSettings(settings, indicator, type) {
	console.log('updateSankeyChartSettings')
	settings.type = type
	//If no seriesDim
	if(settings.seriesDim == null || settings.seriesDim == 'Year') {
		settings.seriesDim = indicator.columns.items.find(col => {
			if(col.sankeyFlag && (settings.xAxisDim == null || col.columnName != settings.xAxisDim.columnName)) {
				//Parent in hierarchies should not be a category when it does not have subTotals - this would cause API error
				if(settings.xAxisDim != null && (settings.xAxisDim.childColumnName != null || settings.xAxisDim.parentColumnName != null) &&
				(col.childColumnName != null || col.parentColumnName != null )){
					null
				} else {
					return col;
				}
			}
		})
	}
	if(settings.seriesDim == null) {
		throw new Error("Indicator categories are not valid for use as a series")
	}
	//If no xAxisDim set it to Year, if no Year, set xaxis to first dim.  Default only the first value for xAxis to selected
	if(settings.xAxisDim == null || settings.xAxisDim.columnName == 'Year' || settings.seriesDim.columnName == settings.xAxisDim.columnName) {
		settings.xAxisDim = indicator.columns.items.find(col => {
			if(col.categoryLabelFlag == 1 &&
				col.columnName !== 'Year' &&
				col.columnName !== settings.seriesDim.columnName &&
				(col.parentColumnName === null || col.parentColumnName !== settings.seriesDim.columnName) &&
				(col.childColumnName === null || col.childColumnName !== settings.seriesDim.columnName)) {
				return col;
			}
		})
	}
	if(settings.xAxisDim == null) {
		throw new Error("Indicator categories are not valid for use on the X Axis")
	}

	return settings;
}

function rebuildSankeyChartSettings(indicator, type, objReq) {
	console.log('rebuildSankeyChartSettings')
	AppliedValues.setupAppliedValues(indicator)
	const req = objReq.request
	let xAxisDim = indicator.columns.items.find(col => col.columnName == req.xAxis)
	let reqSeries = req.categories.find(cat => cat.selected && cat.categoryName !== req.xAxis)
	let seriesDim = indicator.columns.items.find(col => col.columnName == reqSeries.categoryName)

	//AppliedValues.setupAppliedValues(indicator)
	AppliedValues.applyRequestFilters(indicator, type, objReq.request)

	const chartSettings = new ChartSettings(xAxisDim, seriesDim, type)
	return chartSettings
}

function validateRequestProps(obj, indicator) {
	return AppliedValues.validateRequestValues(obj, indicator, ChartTypeEnum.SANKEY.chartName)
}

export {defineChartProps, refreshSankeyChart, getSankeyChartSettings, updateSankeyChartSettings, rebuildSankeyChartSettings, validateRequestProps, ChartSettings}
